import React, { useState } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useCINCOperations } from '../../../hooks/cinc/useCINCOperations';
import { Link2, CheckCircle2, AlertCircle, RotateCw } from 'lucide-react';

interface CINCModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

export const CINCModal: React.FC<CINCModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { connectToCINC, isConnecting, checkCINCStatus, isFetchingStatus } = useCINCOperations();
  const [status, setStatus] = useState<{ integration?: { status: string; enabled: boolean } } | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const handleCheckStatus = async () => {
    if (!selectedOrg?.id) {
      console.error('No selected organization');
      return;
    }

    setIsChecking(true);
    const statusResult = await checkCINCStatus(selectedOrg.id);
    setStatus(statusResult);
    setIsChecking(false);
  };

  const handleConnect = async () => {
    if (!selectedOrg?.id) {
      console.error('No selected organization');
      return;
    }

    const success = await connectToCINC(selectedOrg.id);
    if (success) {
      // Refresh status after successful connection
      await handleCheckStatus();
      // Call onSuccess to refresh parent data
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const isConnected = Boolean(selectedOrg?.cincSetup?.connected);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">CINC Integration</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <Typography variant="p" className="text-gray-600 dark:text-gray-400">
            Connect your CINC CRM account to sync contacts and leads automatically.
          </Typography>

          {/* Status Section */}
          <div className="border border-gray-200 dark:border-white/15 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Typography variant="h5" className="font-semibold">
                Connection Status
              </Typography>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCheckStatus}
                disabled={isChecking || isFetchingStatus}
                className="flex items-center gap-2"
              >
                <RotateCw
                  className={`w-4 h-4 ${isChecking || isFetchingStatus ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
            </div>

            {isChecking || isFetchingStatus ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <RotateCw className="w-4 h-4 animate-spin" />
                <span>Checking status...</span>
              </div>
            ) : isConnected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Configured</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">Not Configured</span>
              </div>
            )}
          </div>

          {/* Connection Instructions */}
          {!isConnected && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4">
              <Typography variant="h5" className="font-semibold mb-2 text-blue-900 dark:text-blue-300">
                How to Connect
              </Typography>
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-900 dark:text-blue-300">
                <li>Click the "Connect to CINC" button below</li>
                <li>A popup window will open for CINC authorization</li>
                <li>Log in to your CINC account and authorize the connection</li>
                <li>The popup will close automatically when complete</li>
              </ol>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between w-full">
          <Button variant="outline" onClick={onClose} type="button">
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleConnect}
            isLoading={isConnecting}
            disabled={isConnecting}
            className="flex items-center gap-2 h-8"
          >
            <Link2 className="w-4 h-4" />
            {isConnected ? 'Reconnect CINC' : 'Connect to CINC'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
